import React, { useEffect, useRef, useState } from 'react';
import { EventSourceMessage, fetchEventSource } from '@microsoft/fetch-event-source';
import { getOrRefreshAccessToken } from '@support/jwt';
import { lastValueFrom } from 'rxjs';
import { useDispatch } from 'react-redux';
import { useOnVisible } from '@support/hooks/use-on-visible';
import { ACTIVITY_API_HOST } from '@support/api';

const supportedEventTypes = ['activity-item-created', 'activity-item-updated'];
const eventTypes = new Set(supportedEventTypes);
const eventDispatchByType = {
  'activity-item-created': 'ACTIVITY_ITEM_CREATED',
  'activity-item-updated': 'ACTIVITY_ITEM_UPDATED',
};

export default function SSEController() {
  const dispatch = useDispatch();
  const [isConnected, setIsConnected] = useState(false);
  const retryRef = useRef(0);

  async function connect() {
    retryRef.current += 1;

    try {
      const token = await lastValueFrom(getOrRefreshAccessToken());
      fetchEventSource(`${ACTIVITY_API_HOST}sse/`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
        onmessage: (message: EventSourceMessage) => {
          if (message.data) {
            const payload = JSON.parse(message.data);
            const eventType = message.event;
            const dispatchType = eventDispatchByType[eventType];

            if (dispatchType && eventTypes.has(eventType)) {
              dispatch({
                type: dispatchType,
                payload,
              });
            }
          }

          if (!isConnected) {
            setIsConnected(true);
          }
        },
        onclose() {
          retryRef.current = 0;
          setIsConnected(false);
        },
        onerror: (err) => {
          if (retryRef.current <= 10) {
            setTimeout(connect, 4000);
          } else {
            console.error('Activity SSE connection failed after 10 retries', err);
          }

          throw new Error('WebSocket error');
        },
      });
    } catch (error) {
      console.error('error connecting to Activity SSE', error);
    }
  }

  useEffect(() => {
    connect();
  }, []);

  useOnVisible(() => {
    if (!isConnected) {
      connect();
    }
  }, [isConnected]);

  return <></>;
}
